import React, { useEffect, useState } from 'react';
import { BrainCircuitIcon, BriefcaseIcon, BuildingIcon, CloudIcon, CoffeeIcon, CupSodaIcon, DropletsIcon, HeartHandshakeIcon, LeafIcon, PackageIcon, SparklesIcon, UsersIcon, WrenchIcon } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Slide 1 — The workplace changed, the break room did not             */
/* ------------------------------------------------------------------ */

const machines: Array<[string, React.ElementType, string]> = [['Coffee', CoffeeIcon, 'Vendor A'], ['Water', DropletsIcon, 'Vendor B'], ['Sparkling', SparklesIcon, 'Vendor C'], ['Tea', LeafIcon, 'Vendor D'], ['Cold brew', CupSodaIcon, 'Vendor E'], ['Soft drinks', PackageIcon, 'Vendor F']];
const workplaceRoles = ['Collaborate', 'Reconnect', 'Recruit talent', 'Welcome customers', 'Build culture'];
export function WorkplaceShiftVisual() {
  const [consolidated, setConsolidated] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setConsolidated(true);
      return;
    }
    const interval = window.setInterval(() => setConsolidated(current => !current), 4200);
    return () => window.clearInterval(interval);
  }, []);
  useEffect(() => {
    const showFinalState = () => setConsolidated(true);
    window.addEventListener('beforeprint', showFinalState);
    return () => window.removeEventListener('beforeprint', showFinalState);
  }, []);
  return <section className="grid min-w-0 flex-1 items-center gap-5 lg:grid-cols-[.85fr_1.15fr] lg:gap-8" aria-label="Consolidating the break room into one platform" data-id="element-2065" data-mp="3usafcmfzm81y">
      <div className="min-w-0" data-id="element-2066" data-mp="3lqj99ghjbz7k">
        <p className="text-[11px] font-bold uppercase tracking-[.18em] text-blue-600" data-id="element-2067" data-mp="3lrm2vw68sfr6">The workplace today</p>
        <p className="mt-2 text-xl font-bold leading-tight text-navy-950 sm:text-2xl" data-id="element-2068" data-mp="1f1ej2g9fh3ga">
          The office is no longer where tasks get done. It is where culture gets built.
        </p>
        <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="What the modern workplace is for" data-id="element-2069" data-mp="1ij27e0v8xhk2">
          {workplaceRoles.map(role => <li key={role} className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-bold text-blue-700" data-id="element-2070" data-mp="1dl0q9lhdrsb8">
              {role}
            </li>)}
        </ul>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-600" data-id="element-2071" data-mp="13muqb9f4ww90">
          Yet the room everyone visits still runs on a wall of disconnected machines — each with its own vendor, service schedule, storage, and failure mode.
        </p>
        <p className="mt-3 text-sm font-bold text-navy-950" data-id="element-2072" data-mp="1rhxerga10xoy">Different machines. Different vendors. Different problems.</p>
      </div>

      <figure className="relative min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-4 sm:p-5" aria-labelledby="workplace-shift-caption" data-id="element-2073" data-mp="1mj2vkd85d74b">
        <div className="flex items-center justify-between gap-2" data-id="element-2074" data-mp="2f81h7kbhared">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-600" data-id="element-2075" data-mp="2f6gpe2gjtmk7">{consolidated ? 'One platform' : 'Six systems'}</p>
          <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[.12em] transition-colors duration-500 ${consolidated ? 'bg-blue-600 text-white' : 'bg-navy-950 text-white'}`} data-id="element-2076" data-mp="2rhr756j3qru0">
            {consolidated ? 'JuLi' : 'The break room wall'}
          </span>
        </div>

        <div className="relative mt-3 min-h-[188px]" data-id="element-2077" data-mp="36gl4cm0670yw">
          <div className={`grid grid-cols-3 gap-2 transition-opacity duration-500 ${consolidated ? 'opacity-0' : 'opacity-100'}`} data-id="element-2078" data-mp="36dyz4t0r4xju">
            {machines.map(([label, Icon, vendor], index) => {
            const column = index % 3;
            const row = Math.floor(index / 3);
            const transform = consolidated ? `translate(${(1 - column) * 105}%, ${(0.5 - row) * 105}%) scale(.35)` : 'translate(0, 0) scale(1)';
            return <div key={label} style={{
              transform,
              transitionDelay: `${index * 45}ms`
            }} className="flex min-h-[88px] flex-col justify-between rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm transition-transform duration-700 ease-[cubic-bezier(.65,0,.35,1)]" data-id="element-2079" data-mp="3u5mag2lj6010" data-mpi={index}>
                  <Icon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-2080" data-mp="zhjzv1d3qd9r" />
                  <div data-id="element-2081" data-mp="bshlc2drl0jh">
                    <p className="text-[11px] font-bold leading-tight text-navy-950" data-id="element-2082" data-mp="brpn0jss8opi">{label}</p>
                    <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-[.1em] text-slate-400" data-id="element-2083" data-mp="6txz00v9dmql">{vendor}</p>
                  </div>
                </div>;
          })}
          </div>

          <div className={`absolute left-1/2 top-1/2 w-[62%] -translate-x-1/2 -translate-y-1/2 rounded-2xl border-2 border-blue-500 bg-navy-950 p-4 text-center text-white shadow-[0_18px_40px_rgba(29,78,216,.3)] transition-all duration-700 ease-[cubic-bezier(.22,1,.36,1)] ${consolidated ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`} aria-hidden={!consolidated} data-id="element-2084" data-mp="grc8thmofd9x">
            <p className="text-[9px] font-bold uppercase tracking-[.18em] text-blue-300" data-id="element-2085" data-mp="24qedv633s1fg">One platform</p>
            <p className="mt-1.5 text-2xl font-bold leading-none" data-id="element-2086" data-mp="1pt5hfxyy07gv">JuLi</p>
            <p className="mt-2 text-[11px] font-semibold leading-snug text-blue-100" data-id="element-2087" data-mp="1uqnkx8u6xuhh">Hundreds of beverages. One vendor. One schedule.</p>
          </div>
        </div>

        <div className="mt-3" data-id="element-2088" data-mp="2ij41feya5eeo">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[.12em]" data-id="element-2089" data-mp="2ngdy9fo00scj">
            <span className="text-slate-500" data-id="element-2090" data-mp="28rq6zll6r4se">Counter space used</span>
            <span className={consolidated ? 'text-blue-700' : 'text-navy-950'} data-id="element-2091" data-mp="2dq1evqrx757z">{consolidated ? '1 footprint' : '6 footprints'}</span>
          </div>
          <div className="mt-1.5 flex h-3 overflow-hidden rounded-full bg-blue-100" data-id="element-2092" data-mp="31eu7bd9c2uxu">
            <span className={`h-full rounded-full bg-navy-950 transition-[width] duration-700 ease-[cubic-bezier(.22,1,.36,1)] ${consolidated ? 'w-[18%]' : 'w-full'}`} data-id="element-2093" data-mp="367dtgym36ye9" />
          </div>
          <p className="mt-1.5 text-[10px] font-semibold text-blue-700" data-id="element-2094" data-mp="3b4j85p92f7n5">{consolidated ? 'Counter space recovered for the room people actually use.' : 'Six machines, six service relationships, one crowded wall.'}</p>
        </div>

        <figcaption id="workplace-shift-caption" className="sr-only" data-id="element-2095" data-mp="324nrj4wqqkfq">
          Six separate beverage machines consolidating into a single JuLi platform, recovering counter space.
        </figcaption>
      </figure>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Slide 2 — Introducing the Workplace Beverage Platform               */
/* ------------------------------------------------------------------ */

const replacedCategories = ['Another coffee machine', 'Another water cooler', 'Another soda dispenser'];
const platformUpdates: Array<[string, string, React.ElementType]> = [['Remote diagnostics', 'Faults surface before anyone reports them.', WrenchIcon], ['Fleet management', 'Every endpoint visible from one console.', CloudIcon], ['Beverage optimization', 'Menus tuned remotely to how each site drinks.', BrainCircuitIcon], ['Automatic updates', 'New capability arrives without a site visit.', SparklesIcon]];
export function WorkplacePlatformVisual() {
  const [activeUpdate, setActiveUpdate] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = window.setInterval(() => setActiveUpdate(current => (current + 1) % platformUpdates.length), 2600);
    return () => window.clearInterval(interval);
  }, []);
  return <section className="grid min-w-0 flex-1 items-center gap-5 lg:grid-cols-[.95fr_1.05fr] lg:gap-8" aria-label="The workplace beverage platform" data-id="element-2096" data-mp="jxjy68bm2wm3">
      <div className="min-w-0" data-id="element-2097" data-mp="3nltzg039gzm3">
        <p className="text-[11px] font-bold uppercase tracking-[.18em] text-blue-300" data-id="element-2098" data-mp="3nl27cz8vhk0k">Category definition</p>
        <ul className="mt-4 space-y-2" aria-label="What JuLi is not" data-id="element-2099" data-mp="2gzkoobfqybef">
          {replacedCategories.map((item, index) => <li key={item} className="relative w-fit text-lg font-bold leading-tight text-slate-400 sm:text-xl" data-id="element-2100" data-mp="2c199rmalhapc" data-mpi={index}>
              <span data-id="element-2101" data-mp="2qucir5a4eg4g">Not {item.toLowerCase()}.</span>
              <span className="pd3-strike absolute left-0 top-1/2 h-[2px] bg-blue-400" style={{
            animationDelay: `${index * 420}ms`
          }} aria-hidden="true" data-id="element-2102" data-mp="2lwkrmdhauy1b" />
            </li>)}
        </ul>
        <p className="mt-5 text-2xl font-bold leading-tight text-white sm:text-3xl" data-id="element-2103" data-mp="2uz2bwmde9glo">
          JuLi replaces them all — <span className="text-blue-300" data-id="element-2104" data-mp="39lvkz9wblq5f">one platform, hundreds of beverages, one operating system.</span>
        </p>
        <div className="mt-5 grid gap-2 sm:grid-cols-3" data-id="element-2105" data-mp="34ovqlw8u2bu5">
          {['Cloud connected', 'AI powered', 'Continuously improving'].map(trait => <p key={trait} className="rounded-xl border border-blue-300/30 bg-blue-400/10 px-3 py-2 text-center text-[11px] font-bold text-blue-100" data-id="element-2106" data-mp="3emjimef2158m">
              {trait}
            </p>)}
        </div>
      </div>

      <figure className="relative min-w-0 overflow-hidden rounded-3xl border border-white/15 bg-white/5 p-4 sm:p-5" aria-labelledby="workplace-platform-caption" data-id="element-2107" data-mp="637v8k28sedm">
        <div className="flex items-center gap-2" data-id="element-2108" data-mp="3t8yuv0fnbpd0">
          <span className="pd3-cloud-pulse flex h-8 w-8 items-center justify-center rounded-xl bg-blue-400 text-navy-950" data-id="element-2109" data-mp="3t7faoyqvcu5y">
            <CloudIcon className="h-4 w-4" aria-hidden="true" data-id="element-2110" data-mp="3ehznfsqjr6v0" />
          </span>
          <div data-id="element-2111" data-mp="3jcdl61solq2u">
            <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-2112" data-mp="3jcvspuksnqq2">Live software layer</p>
            <p className="text-xs font-bold text-white" data-id="element-2113" data-mp="35hl24tdl1rri">Everyone else ships equipment. We ship software.</p>
          </div>
        </div>

        <ol className="mt-4 space-y-2" aria-label="Platform capabilities delivered over the air" data-id="element-2114" data-mp="2vkyg419isunw">
          {platformUpdates.map(([title, detail, Icon], index) => {
          const isActive = index === activeUpdate;
          return <li key={title} className={`flex items-start gap-3 rounded-2xl border p-3 transition-all duration-500 ${isActive ? 'border-blue-300 bg-blue-400/15' : 'border-white/10 bg-navy-950/40'}`} data-id="element-2115" data-mp="2ci4ty2eaymz5" data-mpi={index}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors duration-500 ${isActive ? 'bg-blue-400 text-navy-950' : 'bg-white/5 text-blue-200'}`} data-id="element-2116" data-mp="27g5p0o2wzi03">
                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-2117" data-mp="305w4p1k72ryb" />
                </span>
                <div className="min-w-0" data-id="element-2118" data-mp="2lh8dt8lan6ep">
                  <p className="text-xs font-bold text-white" data-id="element-2119" data-mp="2lg70exgt27tt">{title}</p>
                  <p className="mt-0.5 text-[11px] leading-snug text-slate-300" data-id="element-2120" data-mp="2qf1lk628w8h3">{detail}</p>
                </div>
                <span className={`ml-auto mt-1 h-2 w-2 shrink-0 rounded-full transition-colors duration-500 ${isActive ? 'bg-blue-300' : 'bg-white/15'}`} aria-hidden="true" data-id="element-2121" data-mp="3e4m2kjeu8vnq" />
              </li>;
        })}
        </ol>

        <figcaption id="workplace-platform-caption" className="mt-3 rounded-2xl bg-blue-600 px-3 py-2.5 text-center text-sm font-bold text-white" data-id="element-2122" data-mp="17f0tn087wk4z">
          The machine you install today becomes smarter tomorrow.
        </figcaption>
      </figure>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Slide 3 — Designed for every decision maker                          */
/* ------------------------------------------------------------------ */

type Stakeholder = {
  id: string;
  role: string;
  Icon: React.ElementType;
  priority: string;
  headline: string;
  points: string[];
  proof: string;
};
const stakeholders: Stakeholder[] = [{
  id: 'hr',
  role: 'HR',
  Icon: UsersIcon,
  priority: 'Employee experience',
  headline: 'People gather around beverages.',
  points: ['Coffee starts conversations', 'Sparkling replaces sugary drinks', 'Cold brew fuels the afternoon'],
  proof: 'A daily ritual employees look forward to — a reason the office is worth the commute.'
}, {
  id: 'facilities',
  role: 'Facilities',
  Icon: WrenchIcon,
  priority: 'One relationship',
  headline: 'One machine. One vendor. One schedule.',
  points: ['One inventory system', 'One support team', 'One installation footprint'],
  proof: 'Six service relationships collapse into one accountable partner.'
}, {
  id: 'finance',
  role: 'Finance',
  Icon: BriefcaseIcon,
  priority: 'Lifetime cost',
  headline: 'Fewer systems, lower operating cost.',
  points: ['Less storage and shipping', 'Fewer technician visits', 'Less downtime and waste'],
  proof: 'Cost moves from unpredictable service events to a single, forecastable line.'
}, {
  id: 'operations',
  role: 'Operations',
  Icon: BuildingIcon,
  priority: 'Less complexity',
  headline: 'The platform manages itself.',
  points: ['Remote diagnostics', 'Predictive maintenance', 'Over-the-air improvement'],
  proof: 'Issues get resolved before anyone in the office notices them.'
}, {
  id: 'ceo',
  role: 'CEO',
  Icon: HeartHandshakeIcon,
  priority: 'The signal it sends',
  headline: 'Every visitor experiences your workplace.',
  points: ['Clients and partners', 'Candidates on site', 'Employees every day'],
  proof: 'The beverage experience quietly communicates how much the organization values people.'
}];
export function WorkplaceStakeholdersVisual() {
  const [active, setActive] = useState(stakeholders[0].id);
  const stakeholder = stakeholders.find(option => option.id === active) ?? stakeholders[0];
  const ActiveIcon = stakeholder.Icon;
  return <section className="flex min-w-0 flex-1 flex-col justify-center" aria-label="How each decision maker evaluates the platform" data-id="element-2123" data-mp="2v53vj5t876f4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" role="tablist" aria-label="Decision makers" data-id="element-2124" data-mp="2r27kq19xgelq">
        {stakeholders.map(({
        id,
        role,
        Icon,
        priority
      }) => {
        const isActive = id === active;
        return <button key={id} type="button" role="tab" aria-selected={isActive} onClick={() => setActive(id)} className={`flex flex-col items-start gap-2 rounded-2xl border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isActive ? 'border-blue-500 bg-navy-950 text-white shadow-md' : 'border-slate-200 bg-white text-navy-950 hover:border-blue-300'}`} data-id="element-2125" data-mp="q2vnqtyc2oua">
              <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${isActive ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600'}`} data-id="element-2126" data-mp="3th83ree3efva">
                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-2127" data-mp="2bqa8lfv88n5" />
              </span>
              <span className="text-sm font-bold leading-none" data-id="element-2128" data-mp="1rqwnjwm2cszf">{role}</span>
              <span className={`text-[10px] font-semibold leading-tight ${isActive ? 'text-blue-200' : 'text-slate-500'}`} data-id="element-2129" data-mp="1wphpc0gyrkpz">{priority}</span>
            </button>;
      })}
      </div>

      <article key={stakeholder.id} className="mt-3 grid gap-3 rounded-3xl border border-blue-200 bg-blue-50 p-4 [animation:pd3-rise-in_.4s_ease-out] sm:p-5 lg:grid-cols-[1.05fr_.95fr]" data-id="element-2130" data-mp="21u1y2lqg8ua0">
        <div className="min-w-0" data-id="element-2131" data-mp="2auhzlpnmtrc8">
          <div className="flex items-center gap-2" data-id="element-2132" data-mp="2au8by8q27w9p">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-white" data-id="element-2133" data-mp="2asneobcd1z85">
              <ActiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2134" data-mp="2kk9xotuxx2lg" />
            </span>
            <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-700" data-id="element-2135" data-mp="2kmnqsx6284qd">{stakeholder.role} · {stakeholder.priority}</p>
          </div>
          <h3 className="mt-2 text-xl font-bold leading-tight text-navy-950 sm:text-2xl" data-id="element-2136" data-mp="2ujinrbmopx2j">{stakeholder.headline}</h3>
          <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-2137" data-mp="146js1io6a69p">
            {stakeholder.points.map(point => <li key={point} className="rounded-full border border-blue-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-navy-950" data-id="element-2138" data-mp="106107oma507i">
                {point}
              </li>)}
          </ul>
        </div>
        <p className="self-center rounded-2xl bg-navy-950 p-4 text-sm font-bold leading-snug text-white" data-id="element-2139" data-mp="34a45t64jzgjr">{stakeholder.proof}</p>
      </article>

      <p className="mt-3 text-center text-sm font-bold text-blue-700 sm:text-base" data-id="element-2140" data-mp="3e6qmrjtrdngi">
        Maximum choice. Minimum space. One platform every stakeholder can approve.
      </p>
    </section>;
}